// Guess the colour — a test.
//
// The picture arrives in the wrong colour and you have to say what the real
// one is. The trick is doing it to one region and not the whole picture:
// Luffy's vest without his hat's ribbon, a single grey streak without the rest
// of the hair.
//
// How it works, since that was the question:
//   1. The image is drawn to a canvas, so its pixels can be read. These are
//      the same MyAnimeList URLs the rest of the site uses, served with CORS,
//      which is why the poster maker can already read them.
//   2. Each round carries a seed point — a spot inside the region, stored as a
//      fraction of the image's width and height so it survives any size.
//   3. From that seed we flood outwards, taking every touching pixel whose
//      colour is within a tolerance of it. Anime art is flat colour with hard
//      edges, so a region separates itself: the vest stops at the outline, the
//      streak stops where the black hair begins.
//   4. The true colour is the average of what we flooded. Nothing is typed in
//      by hand, so it cannot disagree with the picture.
//   5. For the question we repaint those pixels in a decoy hue, keeping each
//      pixel's own lightness so the shading and the outlines survive.
//
// Not linked from anywhere and marked noindex: it is here to be tried, not
// found.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './ColorGuess.css';

const ROUNDS = [
  // One flood takes one connected panel, which is rarely the whole thing a
  // person means: the left half of a vest, a single band of a hat. Each round
  // therefore carries several seeds and their regions are joined.
  //
  // None of these were guessed. A probe flooded from each seed, painted what
  // it caught in magenta over the picture, and the sheet was looked at before
  // the seed was kept — which is how the first set was caught colouring
  // Chopper's face instead of his fur and the sky instead of Zoro's shirt.
  {
    name: 'Monkey D. Luffy', part: 'vest',
    image: 'https://cdn.myanimelist.net/images/characters/9/310307.jpg',
    seeds: [[0.20, 0.70], [0.80, 0.72], [0.33, 0.92]], tolerance: 45,
  },
  {
    name: 'Monkey D. Luffy', part: 'hat band',
    image: 'https://cdn.myanimelist.net/images/characters/9/310307.jpg',
    seeds: [[0.78, 0.10], [0.30, 0.14]], tolerance: 60,          // 7.3%, #da3f33
  },
  {
    name: 'Roronoa Zoro', part: 'hair',
    image: 'https://cdn.myanimelist.net/images/characters/3/100534.jpg',
    seeds: [[0.25, 0.10], [0.14, 0.20], [0.60, 0.03], [0.88, 0.10], [0.05, 0.33]], tolerance: 60,  // 5.4%, #80a760
  },
  {
    name: 'Tony Tony Chopper', part: 'hat',
    image: 'https://cdn.myanimelist.net/images/characters/3/100536.jpg',
    seeds: [[0.25, 0.28], [0.80, 0.30], [0.55, 0.07], [0.10, 0.45]], tolerance: 60,   // 23.7%, #d14146
  },
];

const MAX = 420;   // the canvas the game draws at

// --- colour helpers --------------------------------------------------------

const rgbToHsl = (r, g, b) => {
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
  else if (max === gg) h = ((bb - rr) / d + 2) / 6;
  else h = ((rr - gg) / d + 4) / 6;
  return [h, s, l];
};

const hslToRgb = (h, s, l) => {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue = (p, q, t0) => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue(p, q, h + 1 / 3) * 255),
    Math.round(hue(p, q, h) * 255),
    Math.round(hue(p, q, h - 1 / 3) * 255),
  ];
};

const hex = ([r, g, b]) => `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;

const hexToRgb = (value) => {
  const n = parseInt(value.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// How close two colours look, rather than how close their numbers are: hue
// carries most of the judgement, with lightness and saturation behind it.
const closeness = (a, b) => {
  const [h1, s1, l1] = rgbToHsl(...a);
  const [h2, s2, l2] = rgbToHsl(...b);
  let dh = Math.abs(h1 - h2);
  if (dh > 0.5) dh = 1 - dh;                 // the wheel wraps
  const hueScore = 1 - dh / 0.5;
  const satScore = 1 - Math.abs(s1 - s2);
  const lightScore = 1 - Math.abs(l1 - l2);
  const score = 0.6 * hueScore + 0.2 * satScore + 0.2 * lightScore;
  return Math.max(0, Math.min(100, Math.round(score * 100)));
};

const ColorGuess = () => {
  const canvasRef = useRef(null);
  const original = useRef(null);      // ImageData of the untouched picture
  const maskRef = useRef(null);       // which pixels belong to the region

  const [index, setIndex] = useState(0);
  const [truth, setTruth] = useState(null);
  const [guess, setGuess] = useState({ h: 0, s: 0.8, l: 0.5 });
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState('');

  const round = ROUNDS[index];
  const guessRgb = hslToRgb(guess.h, guess.s, guess.l);

  // --- build the round: read the picture, find the region, repaint it ------
  const prepare = useCallback(() => {
    setLoading(true);
    setFailed('');
    setRevealed(false);
    setScore(null);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => { setFailed('That picture would not load.'); setLoading(false); };
    img.onload = () => {
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);

      let data;
      try {
        data = ctx.getImageData(0, 0, w, h);
      } catch (e) {
        setFailed('That picture cannot be read from this page.');
        setLoading(false);
        return;
      }
      original.current = new ImageData(new Uint8ClampedArray(data.data), w, h);

      // --- flood out from each seed into one shared mask ----------------
      const at = (x, y) => (y * w + x) * 4;
      const tol = round.tolerance;
      const mask = new Uint8Array(w * h);
      let sumR = 0, sumG = 0, sumB = 0, count = 0;

      round.seeds.forEach(([fx, fy]) => {
        const sx = Math.round(fx * w);
        const sy = Math.round(fy * h);
        const start = at(sx, sy);
        const seedColour = [data.data[start], data.data[start + 1], data.data[start + 2]];
        const stack = [[sx, sy]];
        while (stack.length) {
          const [x, y] = stack.pop();
          if (x < 0 || y < 0 || x >= w || y >= h) continue;
          const i = y * w + x;
          if (mask[i]) continue;
          const p = i * 4;
          const dr = data.data[p] - seedColour[0];
          const dg = data.data[p + 1] - seedColour[1];
          const db = data.data[p + 2] - seedColour[2];
          if (Math.sqrt(dr * dr + dg * dg + db * db) > tol) continue;
          mask[i] = 1;
          sumR += data.data[p]; sumG += data.data[p + 1]; sumB += data.data[p + 2];
          count += 1;
          stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
        }
      });

      if (count < 40) {
        setFailed('The region came out too small — the seed point needs moving.');
        setLoading(false);
        return;
      }

      maskRef.current = mask;
      const trueColour = [sumR / count, sumG / count, sumB / count].map(Math.round);
      setTruth(trueColour);

      // --- repaint it in a decoy hue, keeping each pixel's lightness ----
      const [th, ts] = rgbToHsl(...trueColour);
      const decoyH = (th + 0.38 + Math.random() * 0.24) % 1;   // well away from the truth
      const out = ctx.createImageData(data);
      out.data.set(data.data);
      for (let i = 0; i < mask.length; i++) {
        if (!mask[i]) continue;
        const p = i * 4;
        const [, , l] = rgbToHsl(out.data[p], out.data[p + 1], out.data[p + 2]);
        const [r, g, b] = hslToRgb(decoyH, Math.max(0.45, ts), l);
        out.data[p] = r; out.data[p + 1] = g; out.data[p + 2] = b;
      }
      ctx.putImageData(out, 0, 0);
      // Start the sliders on the decoy, so the first move is a change the
      // player made rather than a jump.
      setGuess({ h: decoyH, s: Math.max(0.45, ts), l: 0.5 });
      setLoading(false);
    };
    img.src = round.image;
  }, [round]);

  useEffect(() => { prepare(); }, [prepare]);

  // Repaint the region in whatever colour is being asked for, keeping each
  // pixel's own lightness so the shading and the outlines survive. This runs
  // on every slider move: the character wears your guess while you look for it.
  const paint = useCallback((rgb) => {
    const canvas = canvasRef.current;
    const base = original.current;
    const mask = maskRef.current;
    if (!canvas || !base || !mask) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const out = ctx.createImageData(base);
    out.data.set(base.data);
    const [h, sat] = rgbToHsl(...rgb);
    for (let i = 0; i < mask.length; i++) {
      if (!mask[i]) continue;
      const p = i * 4;
      const [, , l] = rgbToHsl(out.data[p], out.data[p + 1], out.data[p + 2]);
      const [r, g, b] = hslToRgb(h, sat, l);
      out.data[p] = r; out.data[p + 1] = g; out.data[p + 2] = b;
    }
    ctx.putImageData(out, 0, 0);
  }, []);

  // Follow the sliders, until the answer is out.
  useEffect(() => {
    if (!revealed && !loading) paint(hslToRgb(guess.h, guess.s, guess.l));
  }, [guess, revealed, loading, paint]);

  const reveal = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.putImageData(original.current, 0, 0);
    const got = closeness(guessRgb, truth);
    setScore(got);
    setTotal((t) => t + got);
    setRevealed(true);
  };

  const next = () => {
    if (index + 1 < ROUNDS.length) setIndex(index + 1);
    else { setIndex(0); setTotal(0); }
  };

  return (
    <div className="cg-wrap">
      <Helmet>
        <title>Colour test</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="cg-card">
        <p className="cg-step">Round {index + 1} of {ROUNDS.length}</p>
        <h1 className="cg-question">
          What colour is <strong>{round.name}&rsquo;s</strong> {round.part}?
        </h1>

        <div className="cg-stage">
          <canvas ref={canvasRef} className="cg-canvas" />
          {loading && <p className="cg-note">Reading the picture…</p>}
          {failed && <p className="cg-note cg-bad">{failed}</p>}
        </div>

        {!revealed ? (
          <>
            <div className="cg-swatch-row">
              <span className="cg-swatch" style={{ background: hex(guessRgb) }} />
              <code className="cg-hex">{hex(guessRgb)}</code>
            </div>

            <label className="cg-slider">
              <span>Colour</span>
              <input type="range" min="0" max="1" step="0.002" value={guess.h}
                onChange={(e) => setGuess({ ...guess, h: Number(e.target.value) })}
                className="cg-hue" />
            </label>

            <label className="cg-slider">
              <span>Strength</span>
              <input type="range" min="0" max="1" step="0.01" value={guess.s}
                onChange={(e) => setGuess({ ...guess, s: Number(e.target.value) })} />
            </label>

            <label className="cg-slider">
              <span>Light</span>
              <input type="range" min="0.05" max="0.95" step="0.01" value={guess.l}
                onChange={(e) => setGuess({ ...guess, l: Number(e.target.value) })} />
            </label>

            <button className="cg-btn cg-btn-primary" onClick={reveal} disabled={loading || !!failed}>
              Submit
            </button>
          </>
        ) : (
          <>
            <div className="cg-result">
              <div className="cg-compare">
                <div>
                  <span className="cg-swatch big" style={{ background: hex(guessRgb) }} />
                  <small>Your guess</small>
                </div>
                <div>
                  <span className="cg-swatch big" style={{ background: hex(truth) }} />
                  <small>The real one</small>
                </div>
              </div>
              <p className="cg-score">{score}<span>% match</span></p>
              <p className="cg-note">
                Taken from {ROUNDS[index].name}&rsquo;s own pixels: {hex(truth)}
              </p>
            </div>
            <button className="cg-btn cg-btn-primary" onClick={next}>
              {index + 1 < ROUNDS.length ? 'Next character' : `Start again (total ${total})`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ColorGuess;
